import {
  Box,
  Divider,
  HStack,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  useOutsideClick,
  VStack,
} from "@chakra-ui/react";
import { Filter, Plus, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "components/ds";
import {
  FILTERS_REGISTRY,
  generateFilterLabel,
  fullOperatorLabels as operatorLabels,
} from "../filters/filterRegistry";
import { getFilterInput } from "./FilterInputs";
import { FilterOption } from "components/FilterDropdown";
import { FiArrowLeft } from "react-icons/fi";

// Define the types of filters we support
export type FilterType =
  | "location"
  | "jobTitle"
  | "accessLevel"
  | "accessGrantedAt"
  | "lastUsage"
  | "memberStatus"
  | "identityMfaStatus"
  | "authType"
  | "department";

// Define the possible filter operators
export type FilterOperator =
  | "equals"
  | "contains"
  | "doesNotEqual"
  | "doesNotContain"
  | "isOneOf"
  | "before"
  | "after"
  | "isEmpty"
  | "isNotEmpty";

// Define a filter condition
export interface FilterCondition {
  id: string;
  type: FilterType;
  operator: FilterOperator;
  value: any;
  label: string;
}

// Props for the FilterBar component
interface FilterBarProps {
  onAddFilter: (filter: FilterCondition) => void;
  availableFilters?: FilterType[];
  customFilterOptions?: (type: FilterType) => FilterOption[] | undefined;
}

// Styled animated tag for our filter chips

// FilterPopover component for adding/editing filters - COMPACT & SLEEK VERSION
const FilterPopover: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onAddFilter: (filter: Omit<FilterCondition, "id" | "label">) => void;
  availableFilters: FilterType[];
  initialFocusRef: React.RefObject<HTMLButtonElement>;
  customFilterOptions?: (type: FilterType) => FilterOption[] | undefined;
}> = ({
  isOpen,
  onClose,
  onAddFilter,
  availableFilters,
  initialFocusRef,
  customFilterOptions,
}) => {
  const [selectedType, setSelectedType] = useState<FilterType | null>(null);
  const [selectedOperator, setSelectedOperator] =
    useState<FilterOperator | null>(null);
  const [filterValue, setFilterValue] = useState<any>(null);

  // Reset state when popover closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedType(null);
      setSelectedOperator(null);
      setFilterValue(null);
    }
  }, [isOpen]);

  const handleAddFilter = () => {
    if (selectedType && selectedOperator) {
      // For isEmpty and isNotEmpty, set value to true
      const value =
        selectedOperator === "isEmpty" || selectedOperator === "isNotEmpty"
          ? true
          : filterValue;

      onAddFilter({
        type: selectedType,
        operator: selectedOperator,
        value: value,
      });
      onClose();
    }
  };

  // Filter type selection screen
  if (!selectedType) {
    return (
      <Box p={0} width="240px">
        <Text
          fontWeight="500"
          mb={1}
          fontSize="xs"
          color="text.body.primary"
          p={2}
        >
          Add filter
        </Text>
        <Divider mb={1} borderColor="border.subtle" />
        <VStack align="stretch" spacing={0} mt={1}>
          {availableFilters.map((filterType) => {
            const filterConfig = FILTERS_REGISTRY[filterType];
            return (
              <Box
                key={filterType}
                as="button"
                display="flex"
                alignItems="center"
                width="100%"
                px={2}
                py={1}
                justifyContent="flex-start"
                borderRadius="none"
                bg="transparent"
                _hover={{ bg: "bg.section" }}
                onClick={() => setSelectedType(filterType)}
                ref={
                  filterType === availableFilters[0]
                    ? initialFocusRef
                    : undefined
                }
              >
                <Icon
                  as={filterConfig.icon}
                  boxSize={3}
                  color="text.body.tertiary"
                  mr={2}
                />
                <Text
                  fontWeight="400"
                  fontSize="xs"
                  color="text.body.secondary"
                >
                  {filterConfig.label}
                </Text>
              </Box>
            );
          })}
        </VStack>
      </Box>
    );
  }

  // Operator selection screen
  if (!selectedOperator) {
    const filterConfig = FILTERS_REGISTRY[selectedType];
    const operators = filterConfig?.operators || [];

    return (
      <Box p={0} width="240px">
        <HStack mb={1} align="center" spacing={2} pt={2} pl={2}>
          <Box
            as="button"
            display="flex"
            alignItems="center"
            justifyContent="center"
            h="16px"
            w="16px"
            borderRadius="sm"
            onClick={() => setSelectedType(null)}
            _hover={{ bg: "bg.section" }}
          >
            <Icon as={FiArrowLeft} boxSize={2.5} color="text.body.tertiary" />
          </Box>
          <Text fontWeight="500" fontSize="xs" color="text.body.primary">
            {filterConfig.label}
          </Text>
        </HStack>
        <Divider mb={1} borderColor="border.subtle" />
        <VStack align="stretch" spacing={0} mt={1}>
          {operators.map((operator) => (
            <Box
              key={operator}
              as="button"
              display="flex"
              alignItems="center"
              width="100%"
              px={2}
              py={1}
              justifyContent="flex-start"
              borderRadius="none"
              bg="transparent"
              _hover={{ bg: "bg.section" }}
              onClick={() => {
                setSelectedOperator(operator);
                if (operator === "isEmpty" || operator === "isNotEmpty") {
                  setFilterValue(true);
                  // Immediately add filter for isEmpty/isNotEmpty since no value is needed
                  setTimeout(() => {
                    onAddFilter({
                      type: selectedType,
                      operator: operator,
                      value: true,
                    });
                    onClose();
                  }, 50);
                }
              }}
            >
              <Text fontWeight="400" fontSize="xs" color="text.body.secondary">
                {operatorLabels[operator]}
              </Text>
            </Box>
          ))}
        </VStack>
      </Box>
    );
  }

  // Value input screen
  const filterConfig = FILTERS_REGISTRY[selectedType];
  const needsValueInput = !["isEmpty", "isNotEmpty"].includes(selectedOperator);

  // If we have custom options for this filter type from the options service
  const options =
    selectedType && customFilterOptions && customFilterOptions(selectedType)
      ? customFilterOptions(selectedType)
      : filterConfig?.options || [];
  return (
    <Box p={2} width="240px">
      <HStack mb={1} align="center" spacing={2}>
        <Box
          as="button"
          display="flex"
          alignItems="center"
          justifyContent="center"
          h="16px"
          w="16px"
          borderRadius="sm"
          onClick={() => setSelectedOperator(null)}
          _hover={{ bg: "bg.section" }}
        >
          <Icon as={FiArrowLeft} boxSize={2.5} color="text.body.tertiary" />
        </Box>
        <Text fontWeight="500" fontSize="xs" color="text.body.primary">
          {operatorLabels[selectedOperator]}
        </Text>
      </HStack>
      <Divider mb={1} borderColor="border.subtle" />

      {needsValueInput ? (
        <Box px={0} mt={1}>
          {getFilterInput(
            selectedType,
            selectedOperator,
            (value) => {
              // Make sure value is never undefined/null
              const safeValue =
                value === undefined || value === null
                  ? typeof value === "string"
                    ? ""
                    : []
                  : value;

              setFilterValue(safeValue);

              // Add a small delay to ensure the value is set
              setTimeout(() => {
                onAddFilter({
                  type: selectedType,
                  operator: selectedOperator,
                  value: safeValue,
                });
                onClose();
              }, 50);
            },
            () => setSelectedOperator(null),
            options?.flat(),
          )}
        </Box>
      ) : (
        <Button
          size="sm"
          variant="solid"
          tone="primary"
          style={{ width: "100%", marginTop: "4px" }}
          onClick={handleAddFilter}
        >
          Apply filter
        </Button>
      )}
    </Box>
  );
};

// Main FilterBar component - simplified to only handle adding filters
const FilterBar: React.FC<FilterBarProps> = ({
  onAddFilter,
  availableFilters = Object.keys(FILTERS_REGISTRY) as FilterType[],
  customFilterOptions,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const initialFocusRef = useRef<HTMLButtonElement>(null);

  //// Close the popover when clicking outside
  //useOutsideClick({
  //  ref: popoverRef,
  //  handler: onClose,
  //});

  // Handle adding a filter
  const handleAddFilter = useCallback(
    (filter: Omit<FilterCondition, "id" | "label">) => {
      // Ensure we're not adding a filter with null/undefined value
      // (isEmpty/isNotEmpty are exceptions because they don't need a value)
      if (
        (filter.value === null ||
          filter.value === undefined ||
          filter.value === "") &&
        !["isEmpty", "isNotEmpty"].includes(filter.operator)
      ) {
        return;
      }

      const newFilter: FilterCondition = {
        ...filter,
        id: `filter-${Date.now()}`,
        label: generateFilterLabel({ ...filter, id: "", label: "" } as any),
      };

      onAddFilter(newFilter);
      onClose();
    },
    [onAddFilter, onClose],
  );

  return (
    <Box>
      <Popover
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom-start"
        initialFocusRef={initialFocusRef}
        closeOnBlur={false}
        closeOnEsc={false}
      >
        <PopoverTrigger>
          <Button
            ref={triggerRef}
            leadingIcon={<Icon as={Filter} boxSize={3.5} />}
            trailingIcon={<Icon as={Plus} boxSize={3.5} />}
            variant="outline"
            tone="neutral"
            size="md"
            onClick={onOpen}
          >
            Filter
          </Button>
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            ref={popoverRef}
            boxShadow="sm"
            border="1px solid"
            borderColor="border.default"
            borderRadius="sm"
            w="full"
            p={0}
            _focus={{ outline: "none", boxShadow: "sm" }}
          >
            <PopoverBody p={0}>
              <FilterPopover
                isOpen={isOpen}
                onClose={onClose}
                onAddFilter={handleAddFilter}
                availableFilters={availableFilters}
                initialFocusRef={initialFocusRef}
                customFilterOptions={customFilterOptions}
              />
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  );
};

export default FilterBar;
